import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import routes from '../config/routes';
import store from '../config/store';
import ProtectedRoute from './ProtectedRoute';
import RouteComponent from './RouteComponent';
import PageNotFound from './PageNotFound';
import Loader from './shared/Loader';

const Login = lazy(() => import('./auth/Login'));
const Home = lazy(() => import('./home/Home'));
const Users = lazy(() => import('./users/Users'));
const UserEditForm = lazy(() => import('./users/UserEditForm'));

const UserReports = lazy(() => import('./userReports/UserReports'));
const Countries = lazy(() => import('./countries/Countries'));
const Cities = lazy(() => import('./cities/Cities'));
const Spots = lazy(() => import('./spots/Spots'));
const SpotsEdit = lazy(() => import('./spots/spotEdit/SpotEdit'));
const SpotsInteractiveEdit = lazy(() => import('./spots/spotInteractiveEdit/SpotInteractiveEdit'));
const Recommendations = lazy(() => import('./recommendations/Recommendations'));
const RecommendationsEdit = lazy(() =>
  import('./recommendations/RecommendationEditForm')
);
const Lists = lazy(() => import('./lists/Lists'));
const Benefits = lazy(() => import('./benefits/Benefits'));
const BenefitsCategories = lazy(() =>
  import('./benefits/benefitsCategories/BenefitsCategories')
);
const BenefitsCategoryCreate = lazy(() =>
  import(
    './benefits/benefitsCategories/benefitCategoryCreate/BenefitCategoryCreate'
  )
);
const BenefitsCreate = lazy(() => import('./benefits/benefitCreate/BenefitCreateForm'));
const BenefitsEdit = lazy(() => import('./benefits/benefitEdit/BenefitEditForm'));
const Notifications = lazy(() => import('./notifications/Notifications'));
const NotificationCreate = lazy(() =>
  import('./notifications/NotificationCreateFrom')
);
const AutoNotifications = lazy(() =>
  import('./autoNotifications/AutoNotifications')
);
const AutoNotificationsEdit = lazy(() =>
  import('./autoNotifications/AutoNotificationsEdit')
);
const AutoNotificationsReport = lazy(() =>
  import('./autoNotifications/report/AutoNotificationsReport')
);
const AutoNotificationsReportDetail = lazy(() =>
  import('./autoNotifications/report/AutoNotificationsReportDetail')
);
const BusinessOwner = lazy(() => import('./businessOwner/BusinessOwner'));
const Links = lazy(() => import('./links/Links'));
const WebHooks = lazy(() => import('./webhooks/WebHooks'));

export default function AppRoutes() {
  const isLoggedIn = store.useAuth((state) => state.isLoggedIn);
  const location = useLocation();

  return (
    <Routes>
      <Route index element={<Navigate to={routes.login.path} />} />
      <Route
        path={routes.login.path}
        element={
          isLoggedIn ? (
            <Navigate to={location.state?.from || routes.default.path} />
          ) : (
            <RouteComponent pageTitle={routes.login.title}>
              <Suspense fallback={<Loader fullHeight />}>
                <Login />
              </Suspense>
            </RouteComponent>
          )
        }
      />
      <Route element={<ProtectedRoute isAllowed={isLoggedIn} />}>
        <Route
          index
          path={routes.home.path}
          element={
            <RouteComponent pageTitle={routes.home.title}>
              <Suspense fallback={<Loader />}>
                <Home />
              </Suspense>
            </RouteComponent>
          }
        />

        <Route
          index
          path={routes.users.path}
          element={
            <RouteComponent pageTitle={routes.users.title}>
              <Suspense fallback={<Loader />}>
                <Users />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.userEdit.path}
          element={
            <RouteComponent pageTitle={routes.userEdit.title}>
              <Suspense fallback={<Loader />}>
                <UserEditForm />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          path={routes.userReports.path}
          element={
            <RouteComponent pageTitle={routes.userReports.title}>
              <Suspense fallback={<Loader />}>
                <UserReports />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.countries.path}
          element={
            <RouteComponent pageTitle={routes.countries.title}>
              <Suspense fallback={<Loader />}>
                <Countries />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.cities.path}
          element={
            <RouteComponent pageTitle={routes.cities.title}>
              <Suspense fallback={<Loader />}>
                <Cities />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.spots.path}
          element={
            <RouteComponent pageTitle={routes.spots.title}>
              <Suspense fallback={<Loader />}>
                <Spots />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.spotsEdit.path}
          element={
            <RouteComponent
              pageTitle={routes.spotsEdit.title}
              backPath={routes.spotsEdit.backPath}
            >
              <Suspense fallback={<Loader />}>
                <SpotsEdit />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.spotsInteractiveEdit.path}
          element={
            <RouteComponent
              pageTitle={routes.spotsInteractiveEdit.title}
              backPath={routes.spotsInteractiveEdit.backPath}
            >
              <Suspense fallback={<Loader />}>
                <SpotsInteractiveEdit />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.recommendations.path}
          element={
            <RouteComponent pageTitle={routes.recommendations.title}>
              <Suspense fallback={<Loader />}>
                <Recommendations />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.recommendationsEdit.path}
          element={
            <RouteComponent pageTitle={routes.recommendationsEdit.title}>
              <Suspense fallback={<Loader />}>
                <RecommendationsEdit />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.lists.path}
          element={
            <RouteComponent pageTitle={routes.lists.title}>
              <Suspense fallback={<Loader />}>
                <Lists />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.benefits.path}
          element={
            <RouteComponent pageTitle={routes.benefits.title}>
              <Suspense fallback={<Loader />}>
                <Benefits />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.benefitsCategories.path}
          element={
            <RouteComponent
              pageTitle={routes.benefitsCategories.title}
              backPath={routes.benefitsCategories.backPath}
            >
              <Suspense fallback={<Loader />}>
                <BenefitsCategories />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.benefitsCategoryCreate.path}
          element={
            <RouteComponent
              pageTitle={routes.benefitsCategoryCreate.title}
              backPath={routes.benefitsCategoryCreate.backPath}
            >
              <Suspense fallback={<Loader />}>
                <BenefitsCategoryCreate />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.benefitsCategoryEdit.path}
          element={
            <RouteComponent
              pageTitle={routes.benefitsCategoryEdit.title}
              backPath={routes.benefitsCategoryEdit.backPath}
            >
              <Suspense fallback={<Loader />}>
                <BenefitsCategoryCreate />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.benefitsCreate.path}
          element={
            <RouteComponent
              pageTitle={routes.benefitsCreate.title}
              backPath={routes.benefitsCreate.backPath}
            >
              <Suspense fallback={<Loader />}>
                <BenefitsCreate />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.benefitsEdit.path}
          element={
            <RouteComponent
              pageTitle={routes.benefitsEdit.title}
              backPath={routes.benefitsCreate.backPath}
            >
              <Suspense fallback={<Loader />}>
                <BenefitsEdit />
              </Suspense>
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.notifications.path}
          element={
            <RouteComponent pageTitle={routes.notifications.title}>
              <Suspense fallback={<Loader />}>
                <Notifications />
              </Suspense>{' '}
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.notificationCreate.path}
          element={
            <RouteComponent
              pageTitle={routes.notificationCreate.title}
              backPath={routes.notificationCreate.backPath}
            >
              <Suspense fallback={<Loader />}>
                <NotificationCreate />
              </Suspense>{' '}
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.autoNotification.path}
          element={
            <RouteComponent pageTitle={routes.autoNotification.title}>
              <Suspense fallback={<Loader />}>
                <AutoNotifications />
              </Suspense>{' '}
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.autoNotificationEdit.path}
          element={
            <RouteComponent
              pageTitle={routes.autoNotificationEdit.title}
              backPath={routes.autoNotificationEdit.backPath}
            >
              <Suspense fallback={<Loader />}>
                <AutoNotificationsEdit />
              </Suspense>{' '}
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.autoNotificationReport.path}
          element={
            <RouteComponent
              pageTitle={routes.autoNotificationReport.title}
              backPath={routes.autoNotificationReport.backPath}
            >
              <Suspense fallback={<Loader />}>
                <AutoNotificationsReport />
              </Suspense>{' '}
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.autoNotificationReportDetail.path}
          element={
            <RouteComponent
              pageTitle={routes.autoNotificationReportDetail.title}
              backPath={routes.autoNotificationReportDetail.backPath}
            >
              <Suspense fallback={<Loader />}>
                <AutoNotificationsReportDetail />
              </Suspense>{' '}
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.businessOwner.path}
          element={
            <RouteComponent pageTitle={routes.businessOwner.title}>
              <Suspense fallback={<Loader />}>
                <BusinessOwner />
              </Suspense>{' '}
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.links.path}
          element={
            <RouteComponent pageTitle={routes.links.title}>
              <Suspense fallback={<Loader />}>
                <Links />
              </Suspense>{' '}
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.links.path}
          element={
            <RouteComponent pageTitle={routes.links.title}>
              <Suspense fallback={<Loader />}>
                <Links />
              </Suspense>{' '}
            </RouteComponent>
          }
        />
        <Route
          index
          path={routes.webHooks.path}
          element={
            <RouteComponent pageTitle={routes.webHooks.title}>
              <Suspense fallback={<Loader />}>
                <WebHooks />
              </Suspense>{' '}
            </RouteComponent>
          }
        />
      </Route>

      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
}
